import {useCallback, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import * as PropTypes from 'prop-types'
import {Burger, Logo} from '@components/lib'
import {matchPath, useLocation} from 'react-router-dom'
import {useQueryParam} from 'use-query-params'

import routes from 'routes'
import {cacheKeys} from 'api'
import {elementIds} from 'constData'
import {localizeRoutePath, translatedUrl} from 'i18n/utils'
import LanguagesDropdown from 'components/LanguagesDropdown'
import {useApi, useNotificationsCount, useProfile} from 'hooks'
import {isProfileConfirmed} from 'utils'
import useNavCollapsed from 'hooks/helpers/useNavCollapsed'
import CustomDropdown from 'components/CustomDropdown'
import NavMenuTrigger from 'components/NavMenuTrigger'

import NotificationItem from '../NotificationItem'
import ProfileNavItem from '../ProfileNavItem'
import {hideNavItems, topNavItems} from '../Navigation.config'

import {
  Container,
  DepositBtnWrapper,
  Item,
  ItemsWrapper,
  LinksWrapper,
  LogoLink,
  OtherActionsWrapper,
  Wrapper,
  LanguagesDropdownWrapper,
  Logospan,
} from './TopNavigation.styles'

const TopNavigation = ({opened, toggleNav}) => {
  const {isCollapsedDown: isCollapsed} = useNavCollapsed('tabletL')
  const {messagesCount} = useNotificationsCount()
  const {profile} = useProfile()
  const isConfirmed = isProfileConfirmed(profile)
  const api = useApi()
  const [buyToken] = useQueryParam('type')
  const location = useLocation()

  const pathMatch = useCallback(
    (paths, exact = true) => {
      const links = paths.reduce((acc, curr) => [...acc, curr.link], [])

      if (Array.isArray(links)) {
        const matchablePaths = links
          .map(localizeRoutePath)
          .map(path =>
            matchPath(
              {
                path,
                exact,
              },
              location.pathname,
            ),
          )
          .filter(match => match)
        return matchablePaths
      }

      return matchPath(
        {
          path: localizeRoutePath(links),
          exact,
        },
        location.pathname,
      )
    },
    [location.pathname],
  )

  const buyTokenRoute = useMemo(
    () =>
      (buyToken && buyToken === 'purchaseToken') ||
      location.pathname.includes('vime-with-mbase') ||
      location.pathname.includes('character-purchase'),
    [buyToken, location],
  )
  const itemFilter = useMemo(
    () =>
      topNavItems.map(item =>
        item.title.toLowerCase() === 'buy'
          ? {...item, isActive: buyTokenRoute}
          : {
              ...item,
              isActive: location.pathname.includes('wallet')
                ? item.link === '/wallet' && !buyTokenRoute
                  ? true
                  : false
                : location.pathname.startsWith('/tokenProductions/')
                  ? item.dropdownNode && pathMatch(item.dropdownOptions).length
                    ? true
                    : false
                  : location.pathname.startsWith('/packages/') &&
                      item.id === elementIds.navigation.items.packages
                    ? item.dropdownNode && pathMatch(item.dropdownOptions)
                      ? true
                      : false
                    : location.pathname.includes('profile')
                      ? item.link === '/profile'
                        ? true
                        : false
                      : location.pathname === item.link,
            },
      ),

    [buyTokenRoute, location.pathname, pathMatch],
  )

  // eslint-disable-next-line
  const updateUserBalance = useQuery(
    [cacheKeys.userMbaseBonus],
    () => api.userMbaseBonus({userId: profile?.id}),
    {
      enabled: profile ? true : false,
    },
  )

  return (
    <Wrapper>
      <Container>
        <LinksWrapper>
          <LogoLink id={elementIds.navigation.items.logo} to={routes.home.path}>
            <Logospan>
              <Logo />
            </Logospan>
          </LogoLink>
          <ItemsWrapper>
            {itemFilter.map(
              ({id, title, Icon, link, exact, isActive, dropdownNode, dropdownOptions}) =>
                dropdownNode ? (
                  <CustomDropdown
                    id={id}
                    key={title}
                    title={title}
                    iconBefore={Icon}
                    options={dropdownOptions}
                    trigger={NavMenuTrigger}
                    placement='bottom-start'
                    $isActive={isActive}
                  />
                ) : (
                  <Item id={id} key={link} to={translatedUrl(link)} $isActive={isActive}>
                    <Icon />
                    {title}
                  </Item>
                ),
            )}
          </ItemsWrapper>
        </LinksWrapper>
        <OtherActionsWrapper>
          <LanguagesDropdownWrapper>
            <LanguagesDropdown id={elementIds.navigation.items.languages} />
          </LanguagesDropdownWrapper>
          <NotificationItem
            id={elementIds.navigation.items.notifications}
            messagesCount={messagesCount}
          />
          {!isCollapsed ? (
            <>
              {!hideNavItems && <DepositBtnWrapper>{isConfirmed ? '' : ''}</DepositBtnWrapper>}
              <ProfileNavItem id={elementIds.navigation.items.profile} />
            </>
          ) : (
            <Burger opened={opened} onClick={toggleNav} id={elementIds.navigation.items.burger} />
          )}
        </OtherActionsWrapper>
      </Container>
    </Wrapper>
  )
}

TopNavigation.propTypes = {
  opened: PropTypes.bool.isRequired,
  toggleNav: PropTypes.func.isRequired,
}

export default TopNavigation
